import {
  Box,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import React, { useMemo, useState } from "react";
import { telemetryProps } from "shared-ui/src/instrumentation/gtm";
import ClickableSection from "@features/impersonation-feature/components/Panel/ClickableSection";
import SearchPopover from "@features/impersonation-feature/components/Panel/SearchPopover";
import useGetCxUserAccounts from "@features/impersonation-feature/hooks/useGetCxUserAccounts";
import {
  ImpersonationSelection,
  ImpersonationState,
  Persona,
} from "../../types";
import {
  createVirtualAdminPersona,
  getWorkingSelection,
  updateSelectionWithPersona,
} from "../../selectionState";
import { useSelectorPopover } from "../../useSelectorPopover";
import { isSamePersona } from "../../personaIdentity";
import UserAvatar from "./UserAvatar";
import {
  CxUsersByTenantRequestDto,
  CxUserTypeFilter,
  ICxUserAccountDto,
} from "@lib/ShiOneClient";
import { StorefrontOutlined } from "@mui/icons-material";
import { getInitials } from "@features/impersonation-feature/helpers";

interface UserImpersonationSelectorProps {
  pending: ImpersonationSelection | null;
  working: ImpersonationSelection | null;
  setImpersonationState: React.Dispatch<
    React.SetStateAction<ImpersonationState>
  >;
}

const LISTBOX_ID = "impersonation-user-options";

const srOnlySx = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
} as const;

function getUserDisplayName(user: Persona | null): string {
  if (!user) {
    return "Select a User";
  }
  const name = [user.firstName, user.lastName].filter(Boolean).join(" ");
  return name || user.email?.split("@")[0] || "Unknown User";
}

function getUserDisplayCaption(user: Persona | null): string | null {
  if (!user) {
    return null;
  }
  const role = user.isVirtualAdmin
    ? "Virtual Admin"
    : user.isAdmin
      ? "Admin"
      : user.isGuest
        ? "Guest"
        : null;
  return [role, user.email].filter(Boolean).join(" • ") || null;
}

function toPersona(user: ICxUserAccountDto): Persona | null {
  if (!user || (!user.active && !user.isGuest)) {
    // Only include active users and guests in the persona list
    // Active and Guest is of type CustomerExperienceStatus
    return null;
  }
  return {
    id: user.id,
    type: user.id !== null ? "guid" : null,
    firstName: user.firstName ?? null,
    lastName: user.lastName ?? null,
    initials: getInitials(
      user.isAdmin ?? false,
      false, // isVirtualAdmin is always false for CxUserAccountDto
      user.isGuest ?? false,
      user.firstName ?? null,
      user.lastName ?? null,
    ),
    email: user.email ?? null,
    photo: user.photo ?? null,
    isGuest: user.isGuest ?? false,
    isAdmin: user.isAdmin ?? false,
    isVirtualAdmin: false,
    shiComEmployeeId: user.shiComEmployeeId ?? null,
  };
}

export default function UserSelector({
  pending,
  working,
  setImpersonationState,
}: UserImpersonationSelectorProps) {
  const [searchString, setSearchString] = useState("");
  const {
    anchorEl,
    isOpen,
    searchInputRef,
    listRef,
    closeSelector,
    handleOpen,
    handleSummaryKeyDown,
    handleSearchKeyDown,
    handleOptionKeyDown,
  } = useSelectorPopover({ pending, setImpersonationState });

  const tenantId = working?.tenant?.id ?? null;

  const { data, isInitialLoading } = useGetCxUserAccounts(
    tenantId != null
      ? new CxUsersByTenantRequestDto({
          tenantId,
          userTypeFilter: CxUserTypeFilter.ActiveOrGuest,
          searchString: searchString || undefined,
          page: 0,
          pageSize: 25,
        })
      : null,
  );

  const personas = useMemo(() => {
    if (tenantId == null) return [];
    const virtualAdmin = createVirtualAdminPersona(working?.account?.id ?? null);
    const result = (data?.items ?? [])
      .map(toPersona)
      .filter((persona): persona is Persona => persona !== null);
    return virtualAdmin ? [virtualAdmin, ...result] : result;
  }, [data?.items, tenantId, working?.account?.id]);

  const selectedUser = working?.user ?? null;
  const selectedUserDisplay = getUserDisplayName(selectedUser);
  const hasTenantContext = tenantId != null;

  const handleSelectUser = (persona: Persona) => {
    setImpersonationState((prev) => {
      const baseSelection = getWorkingSelection(prev);

      // Validate required context before proceeding
      // Cannot select user without a base selection (tenant and account)
      if (!baseSelection || !baseSelection.tenant || !baseSelection.account) {
        return prev;
      }

      const nextSelection: ImpersonationSelection = {
        ...baseSelection,
        user: persona,
      };

      return updateSelectionWithPersona(prev, nextSelection);
    });

    closeSelector();
  };

  return (
    <Box sx={{ width: { xs: "100%", sm: "fit-content" }, minWidth: 0 }}>
      <ClickableSection
        {...telemetryProps({
          name: "impersonation_context_selector",
          section: "impersonation_panel",
          label: "User Selector",
        })}
        onClick={handleOpen}
        onKeyDown={handleSummaryKeyDown}
        role="button"
        tabIndex={0}
        aria-haspopup="listbox"
        aria-controls={LISTBOX_ID}
        aria-expanded={isOpen}
        aria-label={`User selector. Selected: ${selectedUserDisplay}`}
        isActive={isOpen}
      >
        <UserAvatar
          photoUrl={selectedUser?.photo ?? undefined}
          initials={selectedUser?.initials ?? "?"}
          isAdmin={selectedUser?.isAdmin || selectedUser?.isVirtualAdmin}
        />
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: "fontWeightMedium",
              lineHeight: "body2.lineHeight",
              wordBreak: "break-word",
            }}
          >
            {selectedUserDisplay}
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ lineHeight: "caption.lineHeight", display: "block" }}
          >
            {selectedUser?.isVirtualAdmin
              ? "Virtual Admin"
              : selectedUser?.isAdmin
                ? "Admin"
                : selectedUser?.isGuest
                  ? "Guest"
                  : (selectedUser?.email ?? null)}
          </Typography>
        </Box>
      </ClickableSection>

      <SearchPopover
        open={isOpen}
        anchorEl={anchorEl}
        onClose={closeSelector}
        onDebouncedSearchChange={setSearchString}
        onSearchKeyDown={handleSearchKeyDown}
        searchPlaceholder="Search users..."
        searchInputRef={searchInputRef}
        searchInputName="impersonation-user-search"
        searchInputAriaLabel="Search users"
        isSearching={isInitialLoading}
        searchTelemetryProps={telemetryProps({
          name: "search_impersonation_users",
          section: "impersonation_panel",
          label: "Search Users",
        })}
        listId={LISTBOX_ID}
        listAriaLabel="User options"
        listRef={listRef}
        statusMessage={
          !hasTenantContext
            ? "Tenant has no accounts"
            : personas.length === 0
              ? "No users found"
              : `${personas.length} users found`
        }
        isEmpty={personas.length === 0}
        emptyMessage={
          hasTenantContext
            ? "No users found"
            : "Tenant has no accounts"
        }
        statusSx={srOnlySx}
      >
        {personas.map((persona, index) => {
          const isSelected = isSamePersona(selectedUser, persona);
          return (
            <ListItemButton
              key={`${persona.type}-${persona.id}`}
              role="option"
              aria-selected={isSelected}
              selected={isSelected}
              onClick={() => handleSelectUser(persona)}
              onKeyDown={(event) =>
                handleOptionKeyDown(event, index, personas.length)
              }
              sx={{
                gap: 1.25,
                py: { xs: 1.25, sm: 1 },
                px: { xs: 1.5, sm: 2 },
              }}
            >
              <UserAvatar
                photoUrl={persona.photo ?? undefined}
                initials={persona.initials ?? "?"}
                isAdmin={persona.isAdmin || persona.isVirtualAdmin}
                size={28}
              />
              <ListItemText
                primary={getUserDisplayName(persona)}
                secondary={getUserDisplayCaption(persona)}
                slotProps={{
                  primary: {
                    variant: "body2",
                    fontWeight: "fontWeightMedium",
                  },
                  secondary: { variant: "caption" },
                }}
              />
              {persona.shiComEmployeeId && (
                <Tooltip title="shi.com" placement="right">
                  <StorefrontOutlined
                    sx={{
                      fontSize: {
                        xs: (theme) => theme.typography.pxToRem(16),
                        sm: (theme) => theme.typography.pxToRem(18),
                      },
                      color: "text.secondary",
                      ml: "auto",
                      flexShrink: 0,
                    }}
                  />
                </Tooltip>
              )}
              {isSelected && (
                <CheckIcon
                  fontSize="small"
                  sx={{ color: "primary.main", flexShrink: 0 }}
                />
              )}
            </ListItemButton>
          );
        })}
      </SearchPopover>
    </Box>
  );
}
